// @file Surface container size store
import { observeSizeChange } from '@@/bits/resize_observer'
import { observeVisualViewportSize } from '@@/bits/window'
import { BreakPoints, useWindowSizeStore } from '@@/pinia/window_size'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useSurfaceContainerSizeStore = defineStore('surfaceContainerSize', () => {
  const surfaceContainerWidth = ref(useWindowSizeStore().windowWidth)
  const surfaceContainerHeight = ref(useWindowSizeStore().windowHeight)

  const resizeSurfaceContainer = ({ width, height }): void => {
    surfaceContainerWidth.value = width
    surfaceContainerHeight.value = height
  }

  function observeSurfaceContainerSize(surfaceContainerElement: HTMLElement): void {
    observeSizeChange(surfaceContainerElement, (_, contentRect) => {
      resizeSurfaceContainer(contentRect)
    })
    observeVisualViewportSize(() => {
      // On some mobile browsers, e.g. Google Chrome on iOS, when the window resize event is fired on device orientation change,
      // the new width / height is not available in the resize event yet
      // Reference: - https://stackoverflow.com/questions/65648544/clientwidth-and-innerwidth-not-updating-on-ios-chrome-after-orientationchange
      //            - https://stackoverflow.com/questions/12452349/mobile-viewport-height-after-orientation-change
      // We should also check for visual viewport change, this would ensure the window has been resized
      resizeSurfaceContainer(surfaceContainerElement.getBoundingClientRect())
    })
  }

  // BREAKPOINTS
  // If you are looking for something like `isContainerSmallerThanOrEqualTabletLandscape`, negate `isContainerBiggerThanTabletLandscape` instead.
  const containerBiggerThanBreakpoints = {
    isContainerBiggerThanPhone: computed(() => surfaceContainerWidth.value > BreakPoints.Phone),
    isContainerBiggerThanTabletPortrait: computed(() => surfaceContainerWidth.value > BreakPoints.TabletPortrait),
    isContainerBiggerThanTabletLandscape: computed(() => surfaceContainerWidth.value > BreakPoints.TabletLandscape),
    isContainerBiggerThanDesktopSmall: computed(() => surfaceContainerWidth.value > BreakPoints.DesktopSmall),
    isContainerBiggerThanDesktop: computed(() => surfaceContainerWidth.value > BreakPoints.Desktop),
    isContainerBiggerThanDesktopBig: computed(() => surfaceContainerWidth.value > BreakPoints.DesktopBig),
  }
  const containerSmallerThanBreakpoints = {
    isContainerSmallerThanPhone: computed(() => surfaceContainerWidth.value < BreakPoints.Phone),
    isContainerSmallerThanTabletPortrait: computed(() => surfaceContainerWidth.value < BreakPoints.TabletPortrait),
    isContainerSmallerThanTabletLandscape: computed(() => surfaceContainerWidth.value < BreakPoints.TabletLandscape),
    isContainerSmallerThanDesktopSmall: computed(() => surfaceContainerWidth.value < BreakPoints.DesktopSmall),
    isContainerSmallerThanDesktop: computed(() => surfaceContainerWidth.value < BreakPoints.Desktop),
    isContainerSmallerThanDesktopBig: computed(() => surfaceContainerWidth.value < BreakPoints.DesktopBig),
    isContainerSmallerThanDesktop2XL: computed(() => surfaceContainerWidth.value < BreakPoints.Desktop2XL),
    isContainerSmallerThanDesktop3XL: computed(() => surfaceContainerWidth.value < BreakPoints.Desktop3XL),
  }

  const isLandscapePhone = computed(() => {
    return (
      surfaceContainerWidth.value > surfaceContainerHeight.value &&
      // In landscape mode, we should compare the longer edge with the breakpoint
      surfaceContainerWidth.value < BreakPoints.TabletLandscape
    )
  })

  const isTabletPortrait = computed(() => {
    return (
      surfaceContainerWidth.value <= BreakPoints.TabletLandscape &&
      surfaceContainerWidth.value >= BreakPoints.TabletPortrait &&
      surfaceContainerWidth.value < surfaceContainerHeight.value
    )
  })

  const isLandscapeTablet = computed(() => {
    return (
      surfaceContainerWidth.value > surfaceContainerHeight.value &&
      // In landscape mode, we should compare the longer edge with the breakpoint
      surfaceContainerWidth.value < BreakPoints.Desktop &&
      surfaceContainerWidth.value >= BreakPoints.TabletLandscape
    )
  })

  // OLD BREAKPOINTS, to be deprecated eventually

  /**
   * @deprecated
   * Check with our designers and align on using new breakpoints only.
   */
  const isPhone = computed(() => surfaceContainerWidth.value < 768)

  /**
   * @deprecated
   * Check with our designers and align on using new breakpoints only.
   */
  const isSmallPhone = computed(() => surfaceContainerWidth.value <= 360)

  return {
    surfaceContainerWidth,
    surfaceContainerHeight,
    resizeSurfaceContainer,
    observeSurfaceContainerSize,
    ...containerBiggerThanBreakpoints,
    ...containerSmallerThanBreakpoints,
    isLandscapePhone,
    isTabletPortrait,
    isLandscapeTablet,

    // OLD BREAKPOINTS
    isPhone,
    isSmallPhone,
  }
})
